import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useRevenueCategories, useCreateRevenueCategory } from '../../hooks/useRevenueCategories.js';
import { useCashboxes } from '../../../cashbox/hooks/useCashboxes.js';
import { useCreateRevenue } from '../../hooks/useRevenues.js';

const METHODS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'transfer', label: 'تحويل' },
  { key: 'external', label: 'خارجي' }
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function defaults() {
  return {
    categoryId: '',
    amount: 0,
    date: todayISO(),
    paymentMethod: 'cash',
    cashboxId: '',
    source: 'manual',
    description: '',
    notes: ''
  };
}

export function RevenueForm({ open, onClose, onSuccess }) {
  const { data: categories = [] } = useRevenueCategories();
  const { data: cashboxes = [] } = useCashboxes();
  const create = useCreateRevenue();
  const createCat = useCreateRevenueCategory();

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: defaults() });

  useEffect(() => {
    if (open) {
      reset(defaults());
      setAdding(false);
      setNewName('');
    }
  }, [open, reset]);

  const method = watch('paymentMethod');

  if (!open) return null;

  async function quickCreateCategory() {
    if (!newName.trim()) return;
    const result = await createCat.mutateAsync({ name: newName.trim(), color: '#22C55E', isActive: true });
    const cat = result?.category || result?.data || result;
    if (cat) {
      setValue('categoryId', cat._id || cat.id);
      setAdding(false);
      setNewName('');
    }
  }

  async function submit(values) {
    if (!values.categoryId || !values.description?.trim()) return;
    if (values.paymentMethod === 'cash' && !values.cashboxId) return;
    const amount = Number(values.amount);
    if (!amount || amount <= 0) return;

    const body = {
      categoryId: values.categoryId,
      amount,
      date: values.date || todayISO(),
      paymentMethod: values.paymentMethod,
      source: 'manual',
      description: values.description.trim(),
      notes: values.notes?.trim() || ''
    };
    if (values.paymentMethod === 'cash' && values.cashboxId) {
      body.cashboxId = values.cashboxId;
    }

    await create.mutateAsync(body, {
      onSuccess: () => {
        onSuccess?.();
        onClose?.();
      }
    });
  }

  // النافذة: درج بارتفاع الشاشة، تذييله لاصق وجسمه هو ما يُمرَّر.
  // كانت الحاوية `grid place-items-stretch` بصفٍّ تلقائي، والصف يتمدّد بطول
  // المحتوى — فيصير `h-full` على النموذج = ارتفاع المحتوى لا ارتفاع النافذة،
  // فلا يتقلّص الجسم ولا يعمل `overflow-y-auto`، ويهبط زرّ الإنشاء تحت حدّ
  // الشاشة بلا شريط تمرير (لا يُرى إلا بتصغير تكبير المتصفح). `flex` لا
  // يتمدّد بالمحتوى، و`min-h-0` يُلغي الحدّ الأدنى التلقائي لعنصر الـ flex
  // الذي كان يمنع تقلّص الجسم.
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch overflow-hidden"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="bg-white border-l border-line shadow-card flex flex-col mr-auto h-full max-h-full min-h-0 overflow-hidden"
        style={{ width: 480, maxWidth: '92vw' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h shrink-0">
          <h3 className="text-[15px] font-bold flex-1">إيراد جديد</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الفئة *</label>
            <div className="flex items-center gap-2">
              <select
                className={clsx('input', errors.categoryId && 'border-red')}
                {...register('categoryId', { required: true })}
              >
                <option value="">— اختر —</option>
                {categories
                  .filter((c) => c.isActive !== false)
                  .map((c) => (
                    <option key={c._id || c.id} value={c._id || c.id}>
                      {c.name}{c.isAuto ? ' (تلقائية)' : ''}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="btn"
                style={{ padding: '8px 10px' }}
                onClick={() => setAdding((v) => !v)}
              >
                <Icon name="plus" size={14} />
              </button>
            </div>
            {adding ? (
              <div className="mt-2 p-2.5 rounded-[10px] bg-paper-2 flex items-center gap-2">
                <input
                  className="input"
                  placeholder="اسم الفئة الجديدة"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={createCat.isPending || !newName.trim()}
                  onClick={quickCreateCategory}
                >
                  إضافة
                </button>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ (د.ع) *</label>
              <input
                className={clsx('input', errors.amount && 'border-red')}
                type="number"
                min="0"
                step="100"
                {...register('amount', { valueAsNumber: true, required: true, min: 1 })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">التاريخ</label>
              <input className="input" type="date" {...register('date')} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الاستلام</label>
            <Controller
              control={control}
              name="paymentMethod"
              render={({ field }) => (
                <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
                  {METHODS.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => {
                        field.onChange(m.key);
                        if (m.key !== 'cash') setValue('cashboxId', '');
                      }}
                      className={clsx(
                        'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                        field.value === m.key
                          ? 'bg-orange text-white shadow-orange-glow'
                          : 'text-ink-2 hover:bg-white'
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {method === 'cash' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة *</label>
              <select
                className={clsx('input', errors.cashboxId && 'border-red')}
                {...register('cashboxId', { required: method === 'cash' })}
              >
                <option value="">— اختر —</option>
                {cashboxes.filter((c) => c.isActive !== false).map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الوصف *</label>
            <input
              className={clsx('input', errors.description && 'border-red')}
              placeholder="ما مصدر هذا الإيراد؟"
              {...register('description', { required: true })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
            <textarea className="input" rows={2} {...register('notes')} />
          </div>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2 shrink-0 bg-white">
          <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>إلغاء</button>
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'جاري...' : 'تسجيل الإيراد'}
          </button>
        </div>
      </form>
    </div>
  );
}
